// Code adapted from https://github.com/colbyhemond/BlackScholesJS/blob/master/js/blackscholes.js
// Minor type-checks by Talos team, including type-related fixes for stdComputeD1 and stdComputeD2

/**
The MIT License (MIT)

Copyright (c) 2014 Stefano Paggi

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

const NormalD = {
  /**
   * from http://www.math.ucla.edu/~tom/distributions/normal.html
   * @param {Number} X
   * @returns {Number|NormalD.normalcdf.D|NormalD.normalcdf.T}
   */
  normalcdf: function (X: number): number {
    //HASTINGS.  MAX ERROR = .000001
    const T = 1 / (1 + 0.2316419 * Math.abs(X));
    const D = 0.3989423 * Math.exp((-X * X) / 2);
    let Prob = D * T * (0.3193815 + T * (-0.3565638 + T * (1.781478 + T * (-1.821256 + T * 1.330274))));
    if (X > 0) {
      Prob = 1 - Prob;
    }
    return Prob;
  },
  /**
   * from http://www.math.ucla.edu/~tom/distributions/normal.html
   * @param {Number} Z x-Value
   * @param {Number} M mean (µ)
   * @param {Number} SD standard deviation
   * @returns {Number|@exp;normalcdf@pro;Prob|@exp;normalcdf@pro;D|@exp;normalcdf@pro;T|normalcdf.Prob|@exp;Math@call;exp|normalcdf.D|@exp;Math@call;abs|normalcdf.T}
   */
  compute: function (Z: number, M: number, SD: number): number {
    let Prob;
    if (SD < 0) {
      throw new Error('The standard deviation must be nonnegative.');
    } else if (SD === 0) {
      if (Z < M) {
        Prob = 0;
      } else {
        Prob = 1;
      }
    } else {
      Prob = NormalD.normalcdf((Z - M) / SD);
      Prob = Math.round(100000 * Prob) / 100000;
    }

    return Prob;
  },
  /**
   * standard normal distribution.
   * @param {Number} Z x-Value
   * @returns {Number|normalcdf.D|normalcdf.T|normalcdf.Prob}
   */
  stdcompute: function (Z: number): number {
    return NormalD.compute(Z, 0, 1);
  },
  /**
   * standard density function
   * @param {type} x Value
   * @returns {Number}
   */
  stdpdf: function (x: number): number {
    const m = Math.sqrt(2 * Math.PI);
    const e = Math.exp(-Math.pow(x, 2) / 2);
    return e / m;
  },
};

/**
 * Call: C = S * N(d1) - K * exp (-rt) * N(d2)
 * Put: P = K * exp (-rt) * N(-d2) - S * N(-d1)
 * @type type
 */
export class BS {
  private static calcD1(h: BSVariables) {
    return (
      (Math.log(h.stock / h.strike) + (h.interest + 0.5 * Math.pow(h.vola, 2)) * h.term) / (h.vola * Math.sqrt(h.term))
    );
  }
  private static calcD2(h: BSVariables) {
    return this.calcD1(h) - h.vola * Math.sqrt(h.term);
  }
  private static calcS(h: BSVariables, phi: number) {
    return -(h.stock * phi * h.vola) / (2 * Math.sqrt(h.term));
  }
  private static calcK(h: BSVariables, d2: number) {
    return h.interest * h.strike * Math.exp(-h.interest * h.term) * NormalD.normalcdf(d2);
  }
  private static calcND2(h: BSVariables, d1: number) {
    return NormalD.normalcdf(d1 - h.vola * Math.sqrt(h.term));
  }
  /**
   * Get the call price
   * @param {input} BSVariables BS holder variables
   * @returns {Number} Fair Price
   */
  public static call(input: BSVariables) {
    const d1 = this.calcD1(input);
    const d2 = this.calcD2(input);
    const res =
      Math.round(
        (input.stock * NormalD.stdcompute(d1) -
          input.strike * Math.exp(-input.interest * input.term) * NormalD.stdcompute(d2)) *
          100
      ) / 100;
    if (isNaN(res)) {
      return 0;
    }
    return res;
  }
  /**
   * Get the put price
   * @param {input} BSVariables BS holder variables
   * @returns {Number} Fair Price
   */
  public static put(input: BSVariables) {
    const d1 = this.calcD1(input);
    const d2 = this.calcD2(input);
    const res =
      Math.round(
        (input.strike * Math.pow(Math.E, -input.interest * input.term) * NormalD.stdcompute(-d2) -
          input.stock * NormalD.stdcompute(-d1)) *
          100
      ) / 100;
    if (isNaN(res)) {
      return 0;
    }
    return res;
  }
}

/**
 * Holder object for BlackScholes variables
 *
 * @param {Float} stock underlying's asset price
 * @param {Float} strike strike price
 * @param {Float} interest annualized risk-free interest rate
 * @param {Float} vola volatility
 * @param {Float} term a time in years
 */
export interface BSVariables {
  stock: number;
  strike: number;
  interest: number;
  vola: number;
  term: number;
}
